import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  buildChallengeCustomSchemeUrl,
  buildChallengePlayStoreUrl,
  buildFirestoreChallengeCustomSchemeUrl,
  buildFirestoreChallengePlayStoreUrl,
} from '../utils/challengeLinks';
import { openChallengeInApp } from '../utils/openChallengeInApp';
import { inAppBrowserLabel, isInAppBrowser } from '../utils/inAppBrowser';
import { websiteAnalytics } from '../utils/analytics';
import { useAndroidDetection } from '../hooks/useAndroidDetection';
import './ChallengePage.css';

export type ChallengeStorageKind = 'remote_config' | 'firestore';

interface ChallengePageProps {
  storage?: ChallengeStorageKind;
}

const SOCIAL_SOURCES = new Set(['facebook', 'facebook_ad', 'instagram', 'whatsapp', 'tiktok', 'youtube']);

function isSocialChallengeSource(source: string): boolean {
  const key = source.trim().toLowerCase();
  return SOCIAL_SOURCES.has(key) || key.includes('facebook');
}

const ChallengePage: React.FC<ChallengePageProps> = ({ storage = 'remote_config' }) => {
  const { challengeId = '' } = useParams<{ challengeId: string }>();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('src') ?? searchParams.get('utm_source') ?? 'website';
  const isAndroid = useAndroidDetection();
  const inAppBrowser = useMemo(() => isInAppBrowser(), []);
  const socialLanding = useMemo(
    () => inAppBrowser || isSocialChallengeSource(source),
    [inAppBrowser, source],
  );
  const browserLabel = useMemo(() => inAppBrowserLabel(), []);
  const autoRedirectStarted = useRef(false);

  const playStoreUrl = useMemo(() => {
    if (!challengeId) return '';
    return storage === 'firestore'
      ? buildFirestoreChallengePlayStoreUrl(challengeId, source)
      : buildChallengePlayStoreUrl(challengeId, source);
  }, [challengeId, source, storage]);

  const customSchemeUrl = useMemo(() => {
    if (!challengeId) return '';
    return storage === 'firestore'
      ? buildFirestoreChallengeCustomSchemeUrl(challengeId, source)
      : buildChallengeCustomSchemeUrl(challengeId, source);
  }, [challengeId, source, storage]);

  const tryOpenApp = useCallback(
    (allowPlayStoreFallback: boolean) => {
      openChallengeInApp({
        customSchemeUrl,
        playStoreUrl,
        fallbackToPlayStore: allowPlayStoreFallback,
      });
    },
    [customSchemeUrl, playStoreUrl],
  );

  useEffect(() => {
    if (!challengeId) return;
    websiteAnalytics.trackPageView(`challenge_${challengeId}`);
  }, [challengeId]);

  useEffect(() => {
    if (!challengeId || !isAndroid || autoRedirectStarted.current || socialLanding) return;

    autoRedirectStarted.current = true;
    websiteAnalytics.trackDownloadClick(source, 'challenge_auto_redirect', challengeId);
    tryOpenApp(true);
  }, [challengeId, isAndroid, socialLanding, source, tryOpenApp]);

  const handleOpenInApp = () => {
    websiteAnalytics.trackDownloadClick(source, 'challenge_open_app', challengeId);
    tryOpenApp(false);
  };

  const handleInstall = () => {
    websiteAnalytics.trackDownloadClick(source, 'challenge_install', challengeId);
    window.location.href = playStoreUrl;
  };

  if (!challengeId) {
    return (
      <div className="challenge-page">
        <h1>Challenge not found</h1>
        <p>Invalid link. Check the URL from the post.</p>
      </div>
    );
  }

  const stickyBar = isAndroid ? (
    <div className="challenge-sticky-cta" role="region" aria-label="Open challenge in app">
      {socialLanding && (
        <p className="challenge-sticky-hint">
          Tap <strong>Open</strong>, then <strong>Continue</strong> on {browserLabel}
        </p>
      )}
      <button type="button" className="challenge-btn primary" onClick={handleOpenInApp}>
        Open in JustFun app
      </button>
      <button type="button" className="challenge-btn secondary" onClick={handleInstall}>
        Install from Play Store
      </button>
    </div>
  ) : null;

  return (
    <div
      className={`challenge-page${socialLanding ? ' challenge-page--social' : ''}${
        isAndroid ? ' challenge-page--android' : ''
      }`}
    >
      <div className="challenge-card">
        <p className="challenge-emoji">🧠</p>
        <h1>Can you solve this?</h1>
        <p className="challenge-id">Challenge: {challengeId}</p>

        {isAndroid ? (
          <p className="challenge-hint">
            {socialLanding
              ? 'Same puzzle for everyone — buttons are pinned at the bottom.'
              : 'Opening JustFun… Use the buttons below if nothing happens.'}
          </p>
        ) : (
          <>
            <p className="challenge-hint">
              Open in the JustFun app to play the exact puzzle from this post.
            </p>
            <p className="challenge-platform">Install JustFun on Android to play this challenge.</p>
            <button type="button" className="challenge-btn primary" onClick={handleInstall}>
              Get JustFun on Google Play
            </button>
            <p className="challenge-footnote">Install on Android to play this exact puzzle.</p>
          </>
        )}
      </div>
      {stickyBar}
    </div>
  );
};

export default ChallengePage;
